import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Tag, Search, Trash2, Plus, Edit, X, AlertCircle 
} from 'lucide-react';
import toast from 'react-hot-toast';
import tagService from '../services/tagService';
import ResponsiveModal, { ResponsiveButton } from './ResponsiveModal';
import EnhancedModal from './EnhancedModal';
import ResponsiveDataTable from './ResponsiveDataTable';
import EnhancedDataTable from './EnhancedDataTableV2';
import CommonLoader from './CommonLoader';
import { CreateButton } from './CommonButtons';

const DataroomTags = ({ dataroomUuid, isUserContext = false }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTagType, setSelectedTagType] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    tag_type: 'file'
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (dataroomUuid) {
      fetchTags();
    }
  }, [dataroomUuid, pagination.current_page, searchTerm, selectedTagType]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedTagType && { tag_type: selectedTagType })
      };

      const response = isUserContext 
        ? await tagService.getTags(dataroomUuid, params)
        : await tagService.getSuperAdminDataroomTags(dataroomUuid, params);
      
      // The API returns the structure with tags array directly
      let tagsData = [];
      if (response) {
        // Check for the actual API response structure
        if (response.tags && Array.isArray(response.tags)) {
          tagsData = response.tags;
        } else if (response.data && Array.isArray(response.data)) {
          tagsData = response.data;
        } else if (Array.isArray(response)) {
          tagsData = response;
        }
      }
      
      setTags(tagsData);
      
      // Handle pagination
      if (response && response.pagination) {
        setPagination({
          current_page: response.pagination.current_page || 1,
          per_page: response.pagination.per_page || 15,
          total: response.pagination.total || tagsData.length,
          last_page: response.pagination.last_page || 1
        });
      } else {
        setPagination({
          current_page: 1,
          per_page: 15,
          total: tagsData.length,
          last_page: 1
        });
      }
    } catch (error) {
      
      // More detailed error message
      if (error.response?.status === 404) {
        toast.error('Tags endpoint not found. Please check API configuration.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized. Please check your permissions.');
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to load tags. Please try again.');
      }
      
      setTags([]);
      setPagination({
        current_page: 1,
        per_page: 15,
        total: 0,
        last_page: 1
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Tag name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Tag name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      errors.name = 'Tag name must not exceed 50 characters';
    } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(formData.name.trim())) {
      errors.name = 'Tag name can only contain letters, numbers, spaces, hyphens and underscores';
    }
    
    if (!formData.tag_type) {
      errors.tag_type = 'Tag type is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = () => {
    setFormData({
      name: '',
      tag_type: 'file'
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const handleEdit = async (tag) => {
    if (!tag || !tag.id) {
      toast.error('Invalid tag data');
      return;
    }
    
    // If we have the tag data from the list, use it directly first
    if (tag.name && tag.tag_type) {
      setSelectedTag(tag);
      setFormData({
        name: tag.name,
        tag_type: tag.tag_type
      });
      setFormErrors({});
      setShowEditModal(true);
      return;
    }
    
    // Otherwise, fetch the full details
    try {
      setLoading(true);
      const response = isUserContext
        ? await tagService.getTag(dataroomUuid, tag.id)
        : await tagService.getSuperAdminDataroomTag(dataroomUuid, tag.id);
      
      // Handle different response structures
      let tagData = null;
      
      // Check various possible response structures
      if (response?.data) {
        // If data is nested
        tagData = response.data.tag || response.data.data || response.data;
      } else if (response?.tag) {
        // If tag is at root level
        tagData = response.tag;
      } else if (response) {
        // Use response directly
        tagData = response;
      }
      
      
      if (!tagData) {
        toast.error('No tag data received');
        return;
      }
      
      setSelectedTag(tagData);
      setFormData({
        name: tagData.name || '',
        tag_type: tagData.tag_type || 'file'
      });
      setFormErrors({});
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load tag details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (tag) => {
    if (!tag || !tag.id) {
      toast.error('Invalid tag data');
      return;
    }
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        name: formData.name.trim(),
        tag_type: formData.tag_type
      };
      
      await (isUserContext 
        ? tagService.createTag(dataroomUuid, data)
        : tagService.createSuperAdminDataroomTag(dataroomUuid, data));
      toast.success('Tag created successfully');
      setShowCreateModal(false);
      setFormErrors({});
      fetchTags();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create tag');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const data = {
        name: formData.name.trim(),
        tag_type: formData.tag_type
      };
      
      await (isUserContext
        ? tagService.updateTag(dataroomUuid, selectedTag.id, data)
        : tagService.updateSuperAdminDataroomTag(dataroomUuid, selectedTag.id, data));
      toast.success('Tag updated successfully');
      setShowEditModal(false);
      setFormErrors({});
      fetchTags();
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update tag');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedTag || !selectedTag.id) {
      toast.error('No tag selected for deletion');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await (isUserContext
        ? tagService.deleteTag(dataroomUuid, selectedTag.id)
        : tagService.deleteSuperAdminDataroomTag(dataroomUuid, selectedTag.id));
      toast.success('Tag deleted successfully');
      setShowDeleteModal(false);
      setSelectedTag(null);
      fetchTags();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete tag');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Tag Name',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-gray-500" />
          <span className="font-medium text-gray-900">{row.name || 'N/A'}</span>
        </div>
      )
    },
    {
      key: 'tag_type',
      header: 'Type',
      sortable: true,
      render: (value, row) => (
        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
          row.tag_type === 'file' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-purple-100 text-purple-800'
        }`}>
          {row.tag_type === 'file' ? 'File' : 'Invitation'}
        </span>
      )
    },
    {
      key: 'created_at',
      header: 'Created Date',
      sortable: true,
      render: (value, row) => {
        const dateStr = row.created_at;
        if (!dateStr) return <span className="text-sm text-gray-400">N/A</span>;
        
        try {
          // Handle the YYYY-MM-DD HH:mm:ss format
          if (typeof dateStr === 'string' && dateStr.includes(' ')) {
            const [date] = dateStr.split(' ');
            return <span className="text-sm text-gray-600">{date}</span>;
          }
          // Handle other date formats
          return <span className="text-sm text-gray-600">{new Date(dateStr).toLocaleDateString()}</span>;
        } catch (error) {
          return <span className="text-sm text-gray-600">{dateStr}</span>;
        }
      }
    }
  ];

  const actions = (tag) => [
    {
      icon: Edit,
      label: 'Edit',
      onClick: () => handleEdit(tag),
      className: 'text-green-600 hover:text-green-800'
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: () => handleDelete(tag),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  if (loading && tags.length === 0) {
    return <CommonLoader />;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Page Header */}
        <div className="border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Tags Management</h2>
              <p className="mt-1 text-sm text-gray-600">
                Manage tags for organizing files in this dataroom
              </p>
            </div>
            <CreateButton
              onClick={handleCreate}
              icon={Tag}
            >
              Create Tag
            </CreateButton>
          </div>
        </div>

        {/* Content Card */}
        <div className="px-6 py-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
            <div className="flex flex-1 gap-4">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Tag Type Filter */}
              <select
                value={selectedTagType}
                onChange={(e) => setSelectedTagType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              >
                <option value="">All Types</option>
                <option value="file">File</option>
                <option value="invitation">Invitation</option>
              </select>
            </div>
          </div>

          {/* Tags Table */}
          <EnhancedDataTable
            columns={columns}
            data={tags}
            actions={actions}
            loading={loading}
            emptyMessage="No tags found. Click 'Create Tag' to add your first tag."
            emptyIcon={Tag}
            showColumnControls={true}
            enableColumnResize={true}
            enableDragDrop={true}
            onSort={(key, direction) => {
              const sorted = [...tags].sort((a, b) => {
                let aVal = a[key];
                let bVal = b[key];
                
                if (key === 'created_at') {
                  aVal = aVal ? new Date(aVal).getTime() : 0;
                  bVal = bVal ? new Date(bVal).getTime() : 0;
                }
                
                if (aVal < bVal) return direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return direction === 'asc' ? 1 : -1;
                return 0;
              });
              setTags(sorted);
            }}
          />
          
          {/* Pagination */}
          {pagination.last_page > 1 && (
            <div className="px-6 py-4 bg-white border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                {pagination.total} tags
              </div>
              <div className="flex items-center gap-1">
                {/* First page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: 1 }))}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="First page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Previous page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                  disabled={pagination.current_page === 1}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Previous page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const maxPages = Math.min(5, pagination.last_page);
                  let startPage = 1;
                  
                  if (pagination.last_page <= 5) {
                    startPage = 1;
                  } else if (pagination.current_page <= 3) {
                    startPage = 1;
                  } else if (pagination.current_page >= pagination.last_page - 2) {
                    startPage = pagination.last_page - 4;
                  } else {
                    startPage = pagination.current_page - 2;
                  }
                  
                  for (let i = 0; i < maxPages; i++) {
                    const pageNum = startPage + i;
                    pages.push(
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, current_page: pageNum }))}
                        className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-all ${
                          pageNum === pagination.current_page
                            ? 'text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        style={pageNum === pagination.current_page ? { backgroundColor: '#3872FA' } : {}}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                  return pages;
                })()}
                
                {/* Next page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Next page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Last page button */}
                <button
                  onClick={() => setPagination(prev => ({ ...prev, current_page: pagination.last_page }))}
                  disabled={pagination.current_page === pagination.last_page}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-500"
                  title="Last page"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    {/* Create Tag Modal */}
    <EnhancedModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormErrors({});
        }}
        title="Create New Tag"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <form onSubmit={handleSubmitCreate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) {
                  setFormErrors({ ...formErrors, name: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Enter tag name"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Use descriptive names to easily identify tags
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => {
                setFormData({ ...formData, tag_type: e.target.value });
                if (formErrors.tag_type) {
                  setFormErrors({ ...formErrors, tag_type: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.tag_type ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
            {formErrors.tag_type && (
              <p className="mt-1 text-sm text-red-600">{formErrors.tag_type}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              File tags are for documents, Invitation tags are for user access
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Create Tag
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setFormErrors({});
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
    </EnhancedModal>

    {/* Edit Tag Modal */}
    <EnhancedModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setFormErrors({});
        }}
        title="Edit Tag"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <form onSubmit={handleSubmitEdit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (formErrors.name) {
                  setFormErrors({ ...formErrors, name: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.name ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="Enter tag name"
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Use descriptive names to easily identify tags
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.tag_type}
              onChange={(e) => {
                setFormData({ ...formData, tag_type: e.target.value });
                if (formErrors.tag_type) {
                  setFormErrors({ ...formErrors, tag_type: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border ${
                formErrors.tag_type ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            >
              <option value="file">File</option>
              <option value="invitation">Invitation</option>
            </select>
            {formErrors.tag_type && (
              <p className="mt-1 text-sm text-red-600">{formErrors.tag_type}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              File tags are for documents, Invitation tags are for user access
            </p>
          </div>
          
          <div className="flex gap-3 pt-4">
            <ResponsiveButton
              type="submit"
              variant="primary"
              loading={isSubmitting}
            >
              Update Tag
            </ResponsiveButton>
            <ResponsiveButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setFormErrors({});
              }}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </form>
    </EnhancedModal>

    {/* Delete Confirmation Modal */}
    <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tag"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to delete the tag "{selectedTag?.name || 'this tag'}"? This action cannot be undone.
            </p>
          </div>
          
          <div className="flex gap-3">
            <ResponsiveButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isSubmitting}
            >
              Delete Tag
            </ResponsiveButton>
            <ResponsiveButton
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </ResponsiveButton>
          </div>
        </div>
    </EnhancedModal>
    </>
  );
};

export default DataroomTags;